import {
  JWTClient,
  type JWTRefreshHook,
  type JWTClientConfig,
} from "@queen-one/jwt-http-client";
// eslint-disable-next-line demo-guard/no-unguarded-requests -- auth plumbing (logout) that must not mint tokens; demo users may log out
import axios from "axios";

import type { AuthUserJWT } from "@/types/auth";

import { armSessionExpiry, reportSessionExpiry } from "@/api/sessionExpiry";
import { blockDemoMutation, isDemoUserFromStorage } from "@/utils/demoMode";

// Store the current client instance
let jwtClientInstance: JWTClient | null = null;
// branddetails holds /setup/default up to ~80 s and forwards this token downstream, so refresh well before exp.
const JWT_REFRESH_BUFFER_SECONDS = 120;
// forceRefresh() queued behind the library's timer refresh is never settled if
// that refresh fails, so never hold a request longer than this for it.
const EARLY_REFRESH_WAIT_MS = 10_000;

/**
 * The library's own request interceptor refreshes only an already-expired
 * token, and its timer can fire late (laptop sleep, throttled background tab).
 * Refresh here instead whenever a request would leave with less than the
 * buffer left. Missing or expired tokens stay the library interceptor's job.
 *
 * forceRefresh() drops the token on any failure. When the refresh endpoint
 * is unreachable or 5xx, the old token still works for up to the buffer, so
 * put it back instead of turning a refresh blip into a logout. A 4xx means the
 * session is gone, so the drop stands and the library's path rejects as usual.
 */
const createJWTClient = (config: JWTClientConfig): JWTClient => {
  const client = new JWTClient(config);
  // Registered after the library's, so axios runs it first.
  client.interceptors.request.use(async (request) => {
    const previous = client.currentJWT;
    // The ambient module types `decoded` against an unresolved AuthUserJWT.
    const expMs =
      ((previous?.decoded as AuthUserJWT | undefined)?.exp ?? 0) * 1000;
    const msLeft = expMs - Date.now();
    if (msLeft <= 0 || msLeft >= JWT_REFRESH_BUFFER_SECONDS * 1000) {
      return request;
    }

    const refreshed = client.forceRefresh().then(
      () => undefined,
      (error: unknown) => {
        const transient =
          !axios.isAxiosError(error) ||
          !error.response ||
          error.response.status >= 500;
        if (transient && !client.currentJWT && expMs > Date.now()) {
          client.currentJWT = previous;
          client.storage.saveTokenToStorage(previous, client.storageKey);
          if (client.autoRefresh !== false) client.scheduleTokenRefresh();
        }
      },
    );
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      refreshed,
      new Promise((resolve) => {
        timer = setTimeout(resolve, EARLY_REFRESH_WAIT_MS);
      }),
    ]);
    clearTimeout(timer);
    return request;
  });
  return client;
};

/**
 * @deprecated Use the new withJWTClient() hight-order function instead.
 */
export const getJWTClient = (
  accessToken?: string,
  refreshHook?: JWTRefreshHook,
): JWTClient | null => {
  const config: JWTClientConfig = {
    storage: localStorage,
    endpointPath: import.meta.env.VITE_JWT_REFRESH_PATH as string,
    // Prevent background refresh timers from leaking noisy retries in tests.
    autoRefresh:
      import.meta.env.MODE !== "test" ? JWT_REFRESH_BUFFER_SECONDS : false,
    refreshHook,
  };

  if (accessToken) {
    config.initialAccessToken = accessToken;
    // A fresh token is a fresh session: whatever expiry the previous one
    // reported must stop suppressing reports for this one.
    armSessionExpiry();
    // Retire the outgoing client before replacing it. Its armed refresh timer
    // writes the same storage key and calls the same hook, so a leftover timer
    // from a previous session can overwrite the token we are about to mint.
    jwtClientInstance?.disableAutoRefresh();
    jwtClientInstance = createJWTClient(config);
  }

  if (!jwtClientInstance) {
    jwtClientInstance = createJWTClient(config);
  }

  return jwtClientInstance;
};

/**
 * Whether `client` is still the installed instance. Lets a caller holding an
 * older client tell "I am still the current session" from "something has
 * replaced me", which is the difference between safely cleaning up after
 * yourself and tearing down somebody else's session.
 */
export const isCurrentJWTClient = (client: JWTClient | null): boolean =>
  client !== null && jwtClientInstance === client;

export const clearJWTClient = (): void => {
  if (jwtClientInstance) {
    // Before dropping the reference, not just clearing storage.
    // clearStoredToken() cancels the armed timer but leaves autoRefresh on, so
    // a timer that already fired would keep re-arming its 5s retry against a
    // client nobody holds any more.
    jwtClientInstance.disableAutoRefresh();
    try {
      jwtClientInstance.clearStoredToken();
    } catch {
      // Storage can be unavailable/restricted in some environments.
      // Continue clearing in-memory client state regardless.
    }
    jwtClientInstance = null;
  }
};

/**
 * Posts a logout request without ever minting a token. Reads the module-private
 * jwtClientInstance directly (not getJWTClient(), which would construct a fresh
 * client) and disables auto-refresh first so a queued timer can't re-persist a
 * token after storage is cleared. Sends a plain axios POST so the bearer-minting
 * request interceptor never runs for the logout call itself.
 */
export async function logoutJWT(url: string): Promise<void> {
  const client = jwtClientInstance;
  if (client) {
    client.disableAutoRefresh();
  }
  const token = client?.currentJWT?.token;
  await axios.post(
    url,
    undefined,
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
  );
}

/**
 * Wraps a function with a JWT client.
 * @param fn - The function to wrap.
 * @returns The result of the function.
 *
 * @usage
 * withJWTClient(client => client.get(`${SETUP_API_BASE_URL}/${siteId}/setup`));
 * );
 */
const MUTATION_METHODS = new Set(["post", "put", "patch", "delete"]);

/**
 * POSTs that render previews without persisting anything — allowed in demo
 * sessions so tiles and editors still show content. Test sends live under
 * /preview/test-send and must NOT match.
 */
const DEMO_ALLOWED_POST_PATTERNS = [
  /\/preview\/frame-variant(\?|$)/,
  /\/previews(\?|$)/,
];

function isDemoAllowedPost(url: unknown): boolean {
  return (
    typeof url === "string" &&
    DEMO_ALLOWED_POST_PATTERNS.some((pattern) => pattern.test(url))
  );
}

/**
 * Read-only safety net for demo sessions: every write verb rejects with a
 * demo notice, so no screen can persist changes. Function values are bound
 * to the real client so its internals (private fields) keep working.
 */
export function demoGuardedClient(client: JWTClient): JWTClient {
  return new Proxy(client, {
    get(target, prop) {
      if (prop === "post") {
        const realPost = (
          Reflect.get(target, prop) as (...args: unknown[]) => Promise<unknown>
        ).bind(target);
        return (...args: unknown[]) =>
          isDemoAllowedPost(args[0]) ? realPost(...args) : blockDemoMutation();
      }
      if (typeof prop === "string" && MUTATION_METHODS.has(prop)) {
        return blockDemoMutation;
      }
      const value: unknown = Reflect.get(target, prop);
      return typeof value === "function"
        ? (value.bind(target) as unknown)
        : value;
    },
  });
}

export async function withJWTClient<T>(
  fn: (
    client: ReturnType<typeof getJWTClient> extends infer C
      ? NonNullable<C>
      : never,
  ) => Promise<T>,
): Promise<T> {
  const client = getJWTClient();

  if (!client) throw new Error("JWT client not available");

  try {
    return await fn(
      isDemoUserFromStorage() ? demoGuardedClient(client) : client,
    );
  } catch (error) {
    // Awaited rather than returned so a failed refresh is seen here, at the
    // one point every API call in the app passes through.
    reportSessionExpiry(error);
    throw error;
  }
}

export type { JWTClient };
export default getJWTClient;
